// Ref: https://github.com/chakra-ui/chakra-ui/blob/main/packages/theme/src/components/input.ts

import { getColor } from '@/util/chakra';
import { inputAnatomy as parts } from '@chakra-ui/anatomy';
import {
    createMultiStyleConfigHelpers,
    defineStyle,
} from '@chakra-ui/styled-system';
import { mode } from '@chakra-ui/theme-tools';

const { definePartsStyle, defineMultiStyleConfig } =
    createMultiStyleConfigHelpers(parts.keys);

const baseStyle = definePartsStyle({
    field: {
        width: '100%',
        minWidth: 0,
        outline: 0,
        position: 'relative',
        appearance: 'none',
        transitionProperty: 'common',
        transitionDuration: 'normal',
        color: 'textDark',
        _placeholder: { color: 'gray.300' },
    },
});
export const size = {
    xl: defineStyle({
        fontSize: 'xl',
        px: 5,
        h: 14,
        borderRadius: 'md',
    }),

    lgr: defineStyle({
        fontSize: 'lgr',
        px: 4,
        h: 12,
        borderRadius: 'md',
    }),

    lg: defineStyle({
        fontSize: 'lg',
        px: 4,
        h: 12,
        borderRadius: 'md',
    }),

    md: defineStyle({
        fontSize: 'md',
        px: 3,
        h: 12,
        borderRadius: 'md',
    }),

    sm: defineStyle({
        fontSize: 'sm',
        px: 3,
        h: 10,
        borderRadius: 'md',
    }),

    xs: defineStyle({
        fontSize: 'xs',
        px: 2,
        h: 6,
        borderRadius: 'sm',
    }),
};

const sizes = {
    xl: definePartsStyle({
        field: size.xl,
        addon: size.xl,
    }),
    lgr: definePartsStyle({
        field: size.lgr,
        addon: size.lgr,
    }),
    lg: definePartsStyle({
        field: size.lg,
        addon: size.lg,
    }),
    md: definePartsStyle({
        field: size.md,
        addon: size.md,
    }),
    sm: definePartsStyle({
        field: size.sm,
        addon: size.sm,
    }),
    xs: definePartsStyle({
        field: size.xs,
        addon: size.xs,
    }),
};

function getDefaults(props: Record<string, any>) {
    const { focusBorderColor: fc, errorBorderColor: ec } = props;
    return {
        focusBorderColor: fc || mode('blue.500', 'blue.300')(props),
        errorBorderColor: ec || mode('red.500', 'red.300')(props),
    };
}

const variantOutline = definePartsStyle((props) => {
    const { theme } = props;
    const { focusBorderColor: fc, errorBorderColor: ec } = getDefaults(props);

    return {
        field: {
            border: '1px solid',
            borderColor: 'border',
            bg: 'white',
            _hover: {
                bg: 'white',
            },
            _readOnly: {
                boxShadow: 'none !important',
                userSelect: 'all',
            },
            _disabled: {
                opacity: 0.4,
                cursor: 'not-allowed',
            },
            _invalid: {
                borderColor: getColor(theme, ec),
                boxShadow: `0 0 0 1px ${getColor(theme, ec)}`,
            },
            _focusVisible: {
                zIndex: 1,
                borderColor: getColor(theme, fc),
                boxShadow: `0 0 0 1px ${getColor(theme, fc)}`,
                bg: 'white',
            },
        },
        addon: {
            border: '1px solid',
            borderColor: mode('inherit', 'whiteAlpha.50')(props),
            bg: mode('gray.100', 'whiteAlpha.300')(props),
        },
    };
});

const variantFilled = definePartsStyle((props) => {
    const { theme } = props;
    const { focusBorderColor: fc, errorBorderColor: ec } = getDefaults(props);

    return {
        field: {
            border: '2px solid',
            borderColor: 'transparent',
            bg: mode('gray.100', 'whiteAlpha.50')(props),
            _hover: {
                bg: mode('gray.50', 'whiteAlpha.100')(props),
            },
            _active: {
                bg: mode('gray.50', 'whiteAlpha.100')(props),
            },
            _readOnly: {
                boxShadow: 'none !important',
                userSelect: 'all',
            },
            _disabled: {
                opacity: 0.4,
                cursor: 'not-allowed',
            },
            _invalid: {
                borderColor: getColor(theme, ec),
            },
            _focusVisible: {
                bg: 'transparent',
                borderColor: getColor(theme, fc),
            },
        },
        addon: {
            border: '2px solid',
            borderColor: 'transparent',
            bg: mode('gray.100', 'whiteAlpha.50')(props),
        },
    };
});

const flushedInputSizeStyles = {
    xl: defineStyle({
        h: 11,
    }),
    lg: defineStyle({
        h: 9,
    }),
    md: defineStyle({
        h: 8,
    }),
    sm: defineStyle({
        h: 7,
    }),
    xs: defineStyle({
        h: 6,
    }),
};

const variantFlushed = definePartsStyle((props) => {
    const { theme, size } = props;
    const { focusBorderColor: fc, errorBorderColor: ec } = getDefaults(props);
    const sizeStyles =
        flushedInputSizeStyles[size as keyof typeof flushedInputSizeStyles];

    return {
        field: {
            borderBottom: '1px solid',
            borderColor: 'gray.300',
            borderRadius: 0,
            px: 0,
            bg: 'transparent',
            ...sizeStyles,
            _readOnly: {
                boxShadow: 'none !important',
                userSelect: 'all',
                borderColor: 'transparent',
                pointerEvents: 'none',
            },
            _invalid: {
                borderColor: getColor(theme, ec),
                boxShadow: `0px 1px 0px 0px ${getColor(theme, ec)}`,
            },
            _focusVisible: {
                borderColor: getColor(theme, fc),
                boxShadow: `0px 1px 0px 0px ${getColor(theme, fc)}`,
            },
        },
        addon: {
            borderBottom: '2px solid',
            borderColor: 'inherit',
            borderRadius: 0,
            px: 0,
            bg: 'transparent',
        },
    };
});

const variantUnstyled = definePartsStyle({
    field: {
        bg: 'transparent',
        px: 0,
        height: 'auto',
    },
    addon: {
        bg: 'transparent',
        px: 0,
        height: 'auto',
    },
});

const variants = {
    outline: variantOutline,
    filled: variantFilled,
    flushed: variantFlushed,
    unstyled: variantUnstyled,
};

const inputTheme = defineMultiStyleConfig({
    baseStyle,
    sizes,
    variants,
    defaultProps: {
        size: 'lgr',
        variant: 'flushed',
    },
});

export default inputTheme;
